import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import { HipaaLogo } from "~/icons";
import { otherInfoSchema } from "~/schemas/SignUp/OtherInfo";
import { useFunnelTracking } from "~/shared/hooks/useFunnelTracking";
import { SignUpFormProps } from "~/shared/utils/types";
import { useSignUpStore } from "~/stores";
import { tw } from "~/utils";
import { ActionButtons } from "../ActionButtons";
import { ErrorMessage } from "../ErrorMessage";
import { FormContentContainer } from "../FormContentContainer";

export const OtherInfoForm = ({
  onPrev,
  onNext,
  stepId = "other_info",
}: SignUpFormProps & { stepId?: string }) => {
  const { patient, setPatientField } = useSignUpStore();
  const { extraInformation } = patient;
  const { trackComplete, trackBack, trackErrors } = useFunnelTracking({
    step: "other_info",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof otherInfoSchema>>({
    mode: "onBlur",
    resolver: zodResolver(otherInfoSchema),
    defaultValues: {
      otherInfo: extraInformation ?? "",
    },
  });

  const onSubmit: SubmitHandler<z.infer<typeof otherInfoSchema>> = async (
    data,
  ) => {
    setPatientField("extraInformation", data.otherInfo);
    await trackComplete();
    // Await the parent: this is the final step, so onNext finalizes the draft
    // (creates the patient) and navigates. Awaiting keeps react-hook-form's
    // isSubmitting true for that whole window, driving the Next spinner.
    await onNext();
  };

  const onError = (errors: FieldErrors) => {
    trackErrors(errors);
  };

  const handlePrev = () => {
    setPatientField("extraInformation", undefined);
    trackBack();
    onPrev();
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      className="rounded-lg shadow-xl"
    >
      <FormContentContainer stepId={stepId}>
        <HipaaLogo className="size-20 self-center" />
        <div>
          <h1 className="font-display text-2xl">
            Do you mind sharing a little bit about your reason for consultation
            so I know which provider of ours is best to pair you with?
          </h1>
        </div>
        <div className={tw("flex w-full flex-col gap-5", !errors && "mb-8")}>
          <textarea
            placeholder="Other information"
            className="w-full rounded-lg border border-gray-300 p-3 placeholder:text-gray-400 focus:ring-0 focus:outline-none"
            {...register("otherInfo")}
          />
        </div>
        {errors.otherInfo && (
          <ErrorMessage message={errors.otherInfo.message ?? ""} />
        )}
      </FormContentContainer>
      <ActionButtons onPrev={handlePrev} isNextLoading={isSubmitting} />
    </form>
  );
};
